<template>
  <div class="campaigns-jobs-index">
    <div v-if="!hasReferralsEnabled" class="alert alert-warning">
      <b-row>
        <b-col cols="12" class="d-flex justify-content-center align-items-center">
          <p>
            <b>The Referrals feature is not enabled in your platform.</b>
          </p>
        </b-col>
        <b-col v-if="isRecruiterAdmin" cols="12" class="d-flex justify-content-center align-items-center">
          <b-button variant="primary" href="mailto:sales@reallinks.io?subject=Enabling Referrals in our platform">Contact Sales to enable this feature</b-button>
        </b-col>
      </b-row>
    </div>
    <div v-else>
      <b-row v-if="showStepper" class="fixed-top bg-white border-bottom">
        <b-col class="py-3">
          <onboarding-stepper
            :options="{
              paddingBottom: false,
              borderBottom: false,
            }"
            :currentStep="currentStep"
            :steps="steps"
          />
        </b-col>
      </b-row>
      <!-- Header -->
      <b-row class="my-3">
        <b-col cols="12" sm="7">
          <h2 class="m-0">{{ $t('jobs.listing-title') }}</h2>
        </b-col>
        <b-col cols="12" sm="5" class="d-flex flex-row justify-content-end align-items-center" v-if="isRecruiter && showAddJobsButton">
          <!-- New Job Button -->
          <b-button v-if="!showStepper" size="md" variant="outline-primary" :to="{ name: 'JobNew' }">
            <font-awesome-icon icon="plus"/>
            <span class="d-none d-sm-inline-block">{{ $t('ctas.plus-new') }}</span>
          </b-button>
          <!-- Add Job By URL -->
          <b-button v-if="isRecruiterAdmin" class="ml-3" size="md" variant="outline-primary" v-b-modal.new-job-by-url-modal>
            <font-awesome-icon icon="plus"/>
            <span class="d-none d-sm-inline-block">{{ $t('campaign.buttons.import-job') }}</span>
          </b-button>
        </b-col>
      </b-row>

      <!-- Add Job By URL Modal -->
      <b-modal
        v-if="isRecruiterAdmin"
        id="new-job-by-url-modal"
        size="xl"
        no-close-on-backdrop
        hide-footer
        :title="$t('content-fetch.titles.create-job')"
        v-show="this.isRecruiter"
      >
        <content-fetch @input="createNewJob($event)"/>
      </b-modal>

      <filter-container
        layout="page"
        :loading="$apollo.loading || !api"
        show-filters
        :results="allJobs"
        @pageUpdated="filterJobsPageUpdated"
        :order="filterJobsOrder"
        @orderUpdated="filterJobsOrderUpdated"
        :orderByEnums="orderByEnums">

          <template v-slot:where>
            <campaign-jobs-filter-where
              v-if="api"
              :campaigns="(api && Array.isArray(api.campaigns) ? api.campaigns : []).filter(({ status }) => status === 'IN_PROGRESS')"
              :whereCustomFields="api ? api.jobAggs : null"
              :value="filterJobsWhere"
              @input="filterJobsWhereUpdated"/>
          </template>
          <template v-slot:before>

            <!-- JOB SCHEDULER -->
            <schedule-linkedin-card v-if="canScheduleLinkedInPosts" class="my-3" scheduleModalID="schedule-campaign-jobs">
              <template v-slot:schedule-posts-modal>
                <schedule-posts-modal
                  :jobIDs="api && Array.isArray(api.jobConnections.edges) ? api.jobConnections.edges.map(({ entry }) => entry.id) : null"
                  @modal-close="() => $bvModal.hide('schedule-campaign-jobs')"/>
              </template>
            </schedule-linkedin-card>

            <!-- PINNED BY EXACT LOCATION -->
            <template v-if="api.pinnedJobsByExactLocation && Array.isArray(api.pinnedJobsByExactLocation.edges) && api.pinnedJobsByExactLocation.edges.length">
              <b-row class="mt-3">
                <b-col><h3><b>{{ $t('jobs.openings-location') }}</b></h3></b-col>
              </b-row>
              <b-row class="mt-3 row-cols-1 row-cols-md-2">
                <template v-for="{ entry } in api.pinnedJobsByExactLocation.edges">
                  <b-col v-if="entry && entry.id" :key="entry.id" v-bind="colProps" class="mb-3">
                    <campaign-job-card :job="entry"/>
                  </b-col>
                </template>
              </b-row>
              <div class="d-flex flex-column justify-content-center align-items-center">
                <p v-if="filterExactLocationJobsPage && api.pinnedJobsByExactLocation.totalPages">
                  {{ $t('manage.page-n-of-t', { page: filterExactLocationJobsPage, totalPages: api.pinnedJobsByExactLocation.totalPages }) }}
                </p>
                <b-pagination :per-page="filterExactLocationJobsLimit" :total-rows="api.pinnedJobsByExactLocation.total" v-model="filterExactLocationJobsPage"/>
              </div>
            </template>

            <!-- PINNED BY COUNTRY -->
            <template v-if="api.pinnedJobsByCountry && Array.isArray(api.pinnedJobsByCountry.edges) && api.pinnedJobsByCountry.edges.length">
              <b-row class="mt-3">
                <b-col><h3><b>{{ $t('jobs.openings-country') }}</b></h3></b-col>
              </b-row>
              <b-row class="mt-3 row-cols-1 row-cols-md-2">
                <template v-for="{ entry } in api.pinnedJobsByCountry.edges">
                  <b-col v-if="entry && entry.id" :key="entry.id" v-bind="colProps" class="mb-3">
                    <campaign-job-card :job="entry"/>
                  </b-col>
                </template>
              </b-row>
              <div class="d-flex flex-column justify-content-center align-items-center">
                <p v-if="filterCountryJobsPage && api.pinnedJobsByCountry.totalPages">
                  {{ $t('manage.page-n-of-t', { page: filterCountryJobsPage, totalPages: api.pinnedJobsByCountry.totalPages }) }}
                </p>
                <b-pagination :per-page="filterCountryJobsLimit" :total-rows="api.pinnedJobsByCountry.total" v-model="filterCountryJobsPage"/>
              </div>
            </template>
          </template>

          <template v-slot:items>
            <!-- REST OF THE JOBS -->
            <template v-if="api.jobConnections && Array.isArray(api.jobConnections.edges) && api.jobConnections.edges.length">
              <b-row class="mt-3">
                <b-col><h3><b>{{ $t('jobs.openings-all') }}</b></h3></b-col>
              </b-row>
              <b-row class="mt-3 row-cols-1 row-cols-md-2">
                <template v-for="{ entry } in api.jobConnections.edges">
                  <b-col v-if="entry && entry.id" :key="entry.id" v-bind="colProps" class="mb-3">
                    <campaign-job-card :job="entry"/>
                  </b-col>
                </template>
              </b-row>
              <div class="d-flex flex-column justify-content-center align-items-center">
                <p v-if="filterCountryJobsPage && api.jobConnections.totalPages">
                  {{ $t('manage.page-n-of-t', { page: filterJobsPage, totalPages: api.jobConnections.totalPages }) }}
                </p>
                <b-pagination :per-page="filterJobsLimit" :total-rows="api.jobConnections.total" v-model="filterJobsPage"/>
              </div>
            </template>
          </template>
      </filter-container>

      <b-row v-if="showStepper" class="fixed-bottom border-top p-2 bg-white">
        <b-col>
          <b-btn class="float-left" variant="outline-primary" @click="backButton">
            Back
          </b-btn>
          <b-btn class="float-right" variant="primary" @click="saveAndNext">
            Next
          </b-btn>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import base64 from 'base-64';
import ms from 'ms';
import enumsMixin from '@src/mixins/enums';
import FilterContainer from '@/components/campaign/FilterContainer';
import CampaignsJobsIndexQuery from '@src/graphql/queries/CampaignsJobsIndex.gql';
import CampaignJobsFilterWhere from '@src/components/campaign/FilterJobsWhere';
import CampaignJobCard from '@src/components/campaign/job-card';
import scheduleLinkedInMixin from '@src/mixins/scheduleLinkedIn';
import connectLinkedInForPosting from '@src/mixins/connectLinkedIn';
import OnboardingStepper from '@/components/profile/OnboardingStepper';
import ContentFetch from '@/components/misc/ContentFetch.vue';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import gql from 'graphql-tag';
import { mapRoot } from '@src/mixins/root';

const { default: confetti } = process.env.VUE_ENV === 'client' ? require('canvas-confetti') : null;

const NewJob = gql`
  mutation JobNew($create: CreateJobInput!) {
    createJob(create: $create) {
      ...JobDetails
    }
  }
  ${JobDetailsFragment}
`;

export default {
  components: {
    CampaignJobCard,
    CampaignJobsFilterWhere,
    FilterContainer,
    ContentFetch,
    OnboardingStepper
  },
  props: {
    currentStep: {
      type: Object,
      default: () => ({
        name: 'campaign-jobs',
        displayName: 'Jobs'
      }),
    },
    steps: {
      type: Array,
      default: () => [
        {
          name: 'company-setup',
          displayName: 'Company Branding',
          stepper: true
        },
        {
          name: 'campaign-jobs',
          displayName: 'Jobs',
          stepper: true
        },
        {
          name: 'campaign-content-share',
          displayName: 'Content',
          stepper: true
        },
        {
          name: 'event-settings',
          displayName: 'Event Settings',
          stepper: true
        }
      ],
    }
  },
  mixins: [
    enumsMixin,
    scheduleLinkedInMixin,
    connectLinkedInForPosting
  ],
  data: () => ({
    err: null,
    filterJobsDefaults: {
      NOT: { isInternal: true },
      project: null,
      status: 'ACTIVE',
    },
    filterJobsWhere: {},
    filterJobsPage: 1,
    filterJobsLimit: 20,
    filterExactLocationJobsPage: 1,
    filterExactLocationJobsLimit: 4,
    filterCountryJobsPage: 1,
    filterCountryJobsLimit: 4,
    filterJobsOrder: 'MOST_RELEVANT',
    showStepper: false
  }),
  created() {
    if (process.env.VUE_ENV === 'client') {
      if (this.$route && this.$route.query && this.$route.query.filter) {
        const { where, order, page, limit } = JSON.parse(base64.decode(this.$route.query.filter));
        this.$log.debug({ where, order, page, limit });

        if (typeof where === 'object') {
          this.filterJobsWhere = { ...this.filterJobsWhere, ...where };
        }
        if (typeof order === 'string') {
          this.filterJobsOrder = order;
        }
        if (typeof page === 'number') {
          this.filterJobsPage = page;
        }
        if (typeof limit === 'number') {
          this.filterJobsLimit = limit;
        }
      } else if (this.$route && this.$route.query && this.$route.query.campaignID) {
        const { campaignID } = this.$route.query;
        this.filterJobsWhere = { ...this.filterJobsWhere, campaigns: { id: campaignID } };
      }
    }
    // if (this.isTaylorAndFrancis) {
    //   this.filterJobsWhere = {
    //     customFields: [
    //       {
    //         key: 'brand',
    //         value: 'Taylor and Francis'
    //       }
    //     ]
    //   };
    // }
    // if (this.isInforma) {
    //   this.filterJobsWhere = {
    //     customFields: [
    //       {
    //         key: 'brand',
    //         value: 'Informa Tech'
    //       }
    //     ]
    //   };
    // }
  },
  async mounted() {
    if (this.$route.query && this.$route.query.j && this.$route.query.comms === 'true') {
      this.filterJobsWhere = { id: this.$route.query.j };
    }

    if (this.$route.query && this.$route.query.callback && !this.$route.hash.includes('error')) {
      const [ callback, jobID ] = this.$route.query.callback.split(':');

      try {
        switch (callback) {
          case 'connectedLinkedInForScheduling': {
            await this.connectedLinkedInForScheduling();
            this.$bvModal.show('schedule-campaign-jobs');
            break;
          }
          case 'connectLinkedInForPosting': {
            this.filterJobsWhere = { id: jobID };
            this.$bvModal.show(`job-share-linkedin-post-editor-${jobID}`);
            await this.connectLinkedInForPosting();
            break;
          }
        }
      } catch (err) {
        this.$log.error(err);
      }
    }
    console.log('Current Query: ', this.$route.query);
    if (this.$route && this.$route.query && this.$route.query.setup && this.$route.query.eventID) {
      this.showStepper = !this.showStepper;
    }
  },
  beforeMount() {
    if (this.isEXPD8 &&
    navigator.geolocation &&
    (!(this.user && this.user.geolocation) ||
    !sessionStorage.getItem('latitude') ||
    !sessionStorage.getItem('longitude'))) {
      navigator.geolocation.getCurrentPosition(
        position => {
          sessionStorage.setItem('latitude', position.coords.latitude);
          sessionStorage.setItem('longitude', position.coords.longitude);
        },
        () => { console.warn('Geolocation failed'); });
    }
  },
  events: {
    capturedLog({ eventName }) {
      if ([ 'JOB_SHARED', 'CONTENT_SHARED' ].includes(eventName)) {
        confetti({ particleCount: 150, spread: 180, zIndex: 1200 });
        if (this.$apollo.queries.dashboard) {
          this.$apollo.queries.dashboard.refresh();
        }
      }
    },
  },
  apollo: {
    api: {
      query: CampaignsJobsIndexQuery,
      variables() {
        const {
          filterJobsDefaults: whereDefaults,
          filterJobsWhere,
          filterJobsPage: page,
          filterJobsLimit: limit,
          filterExactLocationJobsPage,
          filterExactLocationJobsLimit,
          filterCountryJobsPage,
          filterCountryJobsLimit
        } = this;
        const latitude = this.user && this.user.geolocation ? this.user.geolocation.latitude : parseFloat(sessionStorage.getItem('latitude')) || null;
        const longitude = this.user && this.user.geolocation ? this.user.geolocation.longitude : parseFloat(sessionStorage.getItem('latitude')) || null;
        const order = latitude && longitude ? 'DISTANCE_ASC' : this.filterJobsOrder;
        const where = JSON.parse(JSON.stringify(filterJobsWhere));
        if (where && where.customFields) {
          where.customFields = where.customFields.map(el => {
            switch (el.key) {
              case 'brand': el.key = 'brands'; break;
            }
            return el;
          });
        }

        // pin jobs based on user location
        const { location, country } = this.userLocation || {};
        console.log({ location, country });

        return {
          isEmployee: !this.isRecruiter,
          where: (location || country)
            ? {
              ...whereDefaults,
              ...where,
              ...{ NOT: [
                {
                  OR: [
                    ...(location ? [ {
                      customFields: [
                        { key: 'location', value: location }
                      ],
                    } ] : []),
                    ...(country ? [ {
                      customFields: [
                        { key: 'country', value: country }
                      ],
                    } ] : []),
                  ],
                },
                { isInternal: true } ]
              },
            }
            : { ...whereDefaults, ...where },
          pinJobsByExactLocation: !!location,
          pinJobsByCountry: !!country,
          pinExactLocationWhere: {
            ...whereDefaults,
            ...where,
            ...{ AND: [ { customFields: [ { key: 'location', value: location } ] } ] },
          },
          pinCountryWhere: {
            ...whereDefaults,
            ...where,
            ...{ AND: [ { customFields: [ { key: 'country', value: country } ] } ] },
            ...{ NOT: location
              ? [ { customFields: [ { key: 'location', value: location } ] }, { isInternal: true } ]
              : [ { isInternal: true } ]
            },
          },
          order,
          page,
          limit,
          filterExactLocationJobsPage,
          filterExactLocationJobsLimit,
          filterCountryJobsPage,
          filterCountryJobsLimit,
          latitude,
          longitude
        };
      },
      update({ company, user }) {
        if (this.isRecruiter) {
          const { jobAggs, campaigns, jobConnections } = company;
          const { pinnedJobsByExactLocation, pinnedJobsByCountry } = user;
          console.log({ jobAggs });
          console.log({ campaigns, jobConnections, pinnedJobsByExactLocation, pinnedJobsByCountry }, 'RECRUITER');
          return { jobAggs, campaigns, jobConnections, pinnedJobsByExactLocation, pinnedJobsByCountry };
        } else {
          const { jobAggs } = company;
          const { campaigns, jobConnections, pinnedJobsByExactLocation, pinnedJobsByCountry } = user;
          return { campaigns, jobAggs, jobConnections, pinnedJobsByExactLocation, pinnedJobsByCountry };
        }
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      deep: true,
      throttle: ms('1s'),
    },
  },
  metaInfo() {
    return {
      title: this.$t('jobs.listing-title'),
    };
  },
  computed: {
    ...mapRoot([ 'user', 'company', 'isLoggedIn', 'isRecruiter', 'isRecruiterAdmin', 'isTaylorAndFrancis', 'isInforma', 'isEXPD8' ]),
    canScheduleLinkedInPosts() {
      const { sitePreferences, preferences } = this.company || {};
      const { canScheduleLinkedInPosts } = { ...sitePreferences, ...preferences };
      return canScheduleLinkedInPosts === true;
    },
    orderByEnums: () => ({
      'CREATED_DESC': 'Newest first',
      'CREATED_ASC': 'Oldest first',
      'UPDATED_DESC': 'Recently updated',
      'UPDATED_ASC': 'Least updated',
      'RECENTLY_VIEWED': 'Recently viewed',
    }),
    showAddJobsButton() {
      const { sitePreferences, preferences } = this.company || {};
      const { showAddJobsButton = false } = { ...sitePreferences, ...preferences };
      return showAddJobsButton;
    },
    userLocation() {
      const { sitePreferences, preferences } = this.company || {};
      const { pinJobsByLocation } = { ...sitePreferences, ...preferences };
      const { locationMap } = this.company;
      const { customFields } = this.user;
      const userLocation = pinJobsByLocation && locationMap && locationMap.locationField && Array.isArray(customFields)
        ? customFields.find(({ key }) => key === locationMap.locationField) : null;
      const { value: locationValue } = userLocation || {};
      const { location, country } = locationValue && Array.isArray(locationMap.locations)
        ? locationMap.locations.find(({ location }) => location === locationValue) : {};
      return { location, country };
    },
    colProps() {
      switch (parseInt(this.cols, 10)) {
        case 4: return { cols: 12, sm: 6, md: 3 };
        case 3: return { cols: 12, sm: 6, md: 4 };
        case 2: return { cols: 12, sm: 6 };
        default: return { cols: 12 };
      }
    },
    allJobs() {
      if (!this.api) return null;
      const { jobConnections, pinnedJobsByExactLocation, pinnedJobsByCountry } = this.api || {};
      return {
        edges: [
          ...((jobConnections || {}).edges || []),
          ...((pinnedJobsByExactLocation || {}).edges || []),
          ...((pinnedJobsByCountry || {}).edges || [])
        ],
        total: (jobConnections || {}).total || 0 +
          (pinnedJobsByExactLocation || {}).total || 0 +
          (pinnedJobsByCountry || {}).total || 0
      };
    },
    sitePreferences() {
      const { sitePreferences } = this.company || {};
      return sitePreferences || {};
    },
    hasReferralsEnabled() {
      const { featuresEnabled } = this.sitePreferences || {};
      return (featuresEnabled || []).includes('referrals');
    },
  },
  methods: {
    filterJobsWhereUpdated(where) {
      if (JSON.stringify(this.filterJobsWhere) !== JSON.stringify(where)) {
        this.filterJobsWhere = where;
        this.filterJobsPage = 1;
        this.$scrollTo('body');
      }
    },
    filterJobsOrderUpdated(order) {
      this.filterJobsOrder = order;
      this.filterJobsPage = 1;

      this.updateRouteFilter({ page: undefined, order });
      this.$scrollTo('body');
    },
    filterJobsPageUpdated(page) {
      this.filterJobsPage = page;
      this.updateRouteFilter({ page });
      this.$scrollTo('body');
    },
    backButton() {
      this.$router.push(
        {
          name: 'CompanySetup',
          query: { setup: true, eventID: this.$route.query.eventID }
        }
      );
    },
    saveAndNext() {
      this.$router.push(
        {
          name: 'CampaignContentShareIndex',
          query: { setup: true, eventID: this.$route.query.eventID }
        }
      );
    },
    async createNewJob(job) {
      try {
        await this.$apollo.mutate({
          mutation: NewJob,
          variables: { create: job }
        });

        await this.$apollo.queries.api.refetch();
        console.log('This runs so the mutation was executed');
      } catch (err) {
        console.log(err);
      }
    }
  },
  watch: {
    filterJobsWhere: {
      deep: true,
      handler(where) {
        const route = {
          ...this.$route,
          query: {},
        };

        this.filterJobsPage = 1;
        this.filterExactLocationJobsPage = 1;
        this.filterCountryJobsPage = 1;

        if (Object.keys(where).length) {
          route.query.filter = base64.encode(JSON.stringify({ where }));
        }

        if (this.$route.query && this.$route.query.callback) {
          const [ callback ] = this.$route.query.callback.split(':');
          if (callback === 'connectLinkedInForPosting') {
            return;
          }
        }

        if (!this.$route.query.j && !(this.$route.query.comms === 'true')) {
          this.$router.push(route);
        }
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/jobs/CampaignJobsIndex.vue